import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Building2, Calculator, FileCheck, Users, TrendingUp, BarChart3 } from 'lucide-react';

const phoneNumber = '5555981013037'; // WhatsApp number

const solutions = [
  {
    id: 1,
    icon: Building2,
    title: "Societário",
    description: "Cuidamos de toda a parte societária da sua empresa, desde a abertura até alterações contratuais e encerramento, para que você tenha tranquilidade e foco no crescimento do negócio.",
    color: "from-[#0a4b3c] to-[#c99b36]" // verde para dourado
  },
  {
    id: 2,
    icon: Calculator,
    title: "Contabilidade Completa",
    description: "Escrituração contábil, demonstrações financeiras e relatórios gerenciais que apoiam suas decisões estratégicas. Mantemos sua contabilidade sempre em dia e organizada.",
    color: "from-[#0a4b3c] to-[#c99b36]"
  },
  {
    id: 3,
    icon: FileCheck,
    title: "Regularização Fiscal",
    description: "Regularização de débitos fiscais, certidões negativas e adequação às normas tributárias. Mantemos sua empresa em conformidade com todas as obrigações legais.",
    color: "from-[#0a4b3c] to-[#c99b36]"
  },
  {
    id: 4,
    icon: Users,
    title: "Departamento Pessoal",
    description: "Gestão completa de folha de pagamento, admissões, demissões, férias e encargos trabalhistas. Cuidamos de todos os processos com segurança e precisão.",
    color: "from-[#0a4b3c] to-[#c99b36]"
  },
  {
    id: 5,
    icon: TrendingUp,
    title: "Planejamento Tributário",
    description: "Análise detalhada para redução legal de impostos, escolha do melhor regime tributário e aproveitamento de benefícios fiscais disponíveis para sua empresa.",
    color: "from-[#0a4b3c] to-[#c99b36]"
  },
  {
    id: 6,
    icon: BarChart3,
    title: "Consultoria Empresarial",
    description: "Orientação estratégica para crescimento do seu negócio, análise de indicadores financeiros e apoio nas decisões importantes. Parceria para o sucesso da sua empresa.",
    color: "from-[#0a4b3c] to-[#c99b36]"
  }
];

const Solutions = () => {
  const [expandedSolution, setExpandedSolution] = useState(null);

  const toggleSolution = (id) => {
    setExpandedSolution(expandedSolution === id ? null : id);
  };

  const handleCTAClick = (message) => {
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a4b3c] mb-4">
            Soluções Completas para sua Empresa
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Conheça nossos serviços especializados e descubra como podemos ajudar seu negócio a crescer
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <motion.div
                className={`relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer ${
                  expandedSolution === solution.id ? 'ring-2 ring-[#c99b36]' : ''
                }`}
                onClick={() => toggleSolution(solution.id)}
                whileHover={{ y: -5 }}
              >
                {/* Card Header with Gradient */}
                <div className={`bg-gradient-to-br ${solution.color} p-6 text-white relative overflow-hidden`}>
                  <div className="absolute top-0 right-0 opacity-10">
                    {React.createElement(solution.icon, { size: 120 })}
                  </div>
                  <div className="relative z-10">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl mb-4">
                      {React.createElement(solution.icon, { size: 28 })}
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{solution.title}</h3>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <AnimatePresence mode="wait">
                    {expandedSolution === solution.id ? (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <p className="text-gray-700 leading-relaxed mb-4">
                          {solution.description}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCTAClick(encodeURIComponent(`Olá! Gostaria de saber mais sobre ${solution.title}.`));
                          }}
                          className="w-full bg-[#c99b36] hover:bg-[#b0862b] text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-200"
                        >
                          Falar com Especialista
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <p className="text-gray-600 line-clamp-2">
                          {solution.description}
                        </p>
                        <div className="mt-4 flex items-center text-[#c99b36] font-semibold">
                          <span className="text-sm">Clique para saber mais</span>
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Solutions;
